import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NetworkStatus, useApolloClient, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Text } from "@nbs/ui";
import { INDICATORS_CATALOG_QUERY } from "@/features/indicators/api/documents";
import { HubSearchSelect } from "../components/HubSearchSelect";
import { HubPageTitle } from "../components/HubPageTitle";
import {
  exportIndicatorsExcel,
  exportIndicatorsPdf,
  exportIndicatorsWord,
  type ExportIndicatorRow,
  type ExportIndicatorsMeta,
} from "../utils/export-indicators";

type CatalogNode = {
  id: string;
  libelle: string;
  serviceLibelle?: string | null;
  categorie?: string | null;
  frequence?: string | null;
  unite?: string | null;
};

const PAGE_SIZE = 12;

/** Meta / parent SE rows — not selectable as subcategory. */
const EXCLUDED_META_SE = new Set([
  "nbs-se-approvisionnement",
  "nbs-se-services-culturels",
  "nbs-se-valorisation-economique",
  "nbs-se-perceptions",
  "nbs-se-territoire",
  "nbs-se-indicateurs-socio-economiques-transversaux",
  "nbs-se-pressions-menaces",
  "nbs-se-objectifs-developpement-durable",
]);

/** Categories kept out of the hub SE-category filter (survey dumps, etc.). */
const HIDDEN_CATEGORIES = new Set(["enquete"]);

const CATEGORY_LABELS: Record<string, string> = {
  approvisionnement: "Approvisionnement",
  culturel: "Culturel",
  regulation: "Régulation",
  "socio-economique-transversal": "Indicateurs socio-économiques transversaux",
  "pressions-menaces": "Pressions et menaces",
  "objectifs-developpement-durable": "Objectifs de développement durable",
  // legacy alias if a stale payload still uses it
  "socio-culturel": "Indicateurs socio-économiques transversaux",
};

function categoryLabel(code: string): string {
  return CATEGORY_LABELS[code] ?? code;
}

export function HubIndicatorsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "fr-FR";
  const client = useApolloClient();
  const [exporting, setExporting] = useState<"pdf" | "word" | "excel" | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [draftCategorie, setDraftCategorie] = useState("");
  const [draftServiceId, setDraftServiceId] = useState("");
  const [draftFrequence, setDraftFrequence] = useState("");
  const [draftStatut, setDraftStatut] = useState("");
  const [categorie, setCategorie] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [frequence, setFrequence] = useState("");
  const [statut, setStatut] = useState("");
  const [page, setPage] = useState(0);
  const knownTotalRef = useRef(0);

  const filters = useMemo(
    () => ({
      ...(categorie ? { categories: [categorie] } : {}),
      ...(serviceId ? { serviceIds: [serviceId] } : {}),
      ...(frequence ? { frequencies: [frequence] } : {}),
      ...(statut ? { statuses: [statut] } : {}),
    }),
    [categorie, serviceId, frequence, statut],
  );

  const { data, previousData, loading, error, refetch, networkStatus } = useQuery(
    INDICATORS_CATALOG_QUERY,
    {
      variables: {
        filters,
        first: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sort: "libelle_asc",
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    },
  );

  // Keep filter option lists stable across variable changes / refetches.
  const options = data?.indicatorFilterOptions ?? previousData?.indicatorFilterOptions;
  const catalog = data?.indicatorCatalog ?? previousData?.indicatorCatalog;
  const nodes = useMemo(() => {
    const list = (catalog?.nodes ?? []) as CatalogNode[];
    return [...list].sort((a, b) =>
      a.libelle.localeCompare(b.libelle, "fr", { sensitivity: "base", numeric: true }),
    );
  }, [catalog?.nodes]);
  const totalCount = catalog?.totalCount ?? knownTotalRef.current;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const summary = data?.indicatorSummary ?? previousData?.indicatorSummary;
  const paging =
    networkStatus === NetworkStatus.setVariables || networkStatus === NetworkStatus.loading;
  const initialLoading = loading && nodes.length === 0 && totalCount === 0;

  useEffect(() => {
    knownTotalRef.current = 0;
    setPage(0);
  }, [filters]);

  useEffect(() => {
    if (typeof catalog?.totalCount === "number") {
      knownTotalRef.current = catalog.totalCount;
    }
  }, [catalog?.totalCount]);

  useEffect(() => {
    // Only clamp after a real total is known — never while a page request is in flight
    // (otherwise totalCount briefly looks like 0 and snaps back to page 0).
    if (paging || knownTotalRef.current <= 0) return;
    const maxPage = Math.max(0, Math.ceil(knownTotalRef.current / PAGE_SIZE) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [page, paging, catalog?.totalCount]);

  const categoryOptions = useMemo(() => {
    const sorted = ((options?.categories ?? []) as string[])
      .filter((c) => !HIDDEN_CATEGORIES.has(c))
      .map((c) => ({
        value: c,
        label: categoryLabel(c),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));
    return [{ value: "", label: t("hub.indicators.allFeminine") }, ...sorted];
  }, [options?.categories, t]);
  const serviceOptions = useMemo(() => {
    const services = (options?.services ?? []) as Array<{
      id: string;
      libelle: string;
      categorie?: string | null;
    }>;
    const filtered = services
      .filter((s) => {
        if (EXCLUDED_META_SE.has(s.id)) return false;
        if (!draftCategorie) return true;
        return (s.categorie ?? "").trim().toLowerCase() === draftCategorie.trim().toLowerCase();
      })
      .map((s) => ({
        value: s.id,
        label: s.libelle,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));
    return [{ value: "", label: t("hub.indicators.allMasculine") }, ...filtered];
  }, [options?.services, draftCategorie, t]);

  // Disable SE when the selected category has no selectable subcategory SE.
  const serviceFilterDisabled = Boolean(draftCategorie) && serviceOptions.length <= 1;

  useEffect(() => {
    if (serviceFilterDisabled && draftServiceId) setDraftServiceId("");
  }, [serviceFilterDisabled, draftServiceId]);

  useEffect(() => {
    // Wait until services are loaded — otherwise a refetch briefly empties
    // options and would wipe a valid draft selection.
    if (!draftServiceId || !options?.services || serviceFilterDisabled) return;
    const stillValid = serviceOptions.some((o) => o.value === draftServiceId);
    if (!stillValid) setDraftServiceId("");
  }, [draftServiceId, serviceOptions, options?.services, serviceFilterDisabled]);

  const frequencyOptions = useMemo(
    () => [
      { value: "", label: t("hub.indicators.allFeminine") },
      ...((options?.frequencies ?? []) as string[]).map((f) => ({ value: f, label: f })),
    ],
    [options?.frequencies, t],
  );
  const statusOptions = useMemo(
    () => [
      { value: "", label: t("hub.indicators.allMasculine") },
      ...((options?.statuses ?? []) as string[]).map((s) => ({ value: s, label: s })),
    ],
    [options?.statuses, t],
  );

  function applyFilters() {
    setCategorie(draftCategorie);
    setServiceId(serviceFilterDisabled ? "" : draftServiceId);
    setFrequence(draftFrequence);
    setStatut(draftStatut);
  }

  function resetFilters() {
    setDraftCategorie("");
    setDraftServiceId("");
    setDraftFrequence("");
    setDraftStatut("");
    setCategorie("");
    setServiceId("");
    setFrequence("");
    setStatut("");
  }

  const buildExportMeta = useCallback((): ExportIndicatorsMeta => {
    const filterParts: string[] = [];
    if (categorie)
      filterParts.push(`${t("hub.indicators.seCategory")}: ${categoryLabel(categorie)}`);
    if (serviceId) {
      const se = serviceOptions.find((o) => o.value === serviceId)?.label ?? serviceId;
      filterParts.push(`${t("hub.indicators.service")}: ${se}`);
    }
    if (frequence) filterParts.push(`${t("hub.indicators.frequency")}: ${frequence}`);
    if (statut) filterParts.push(`${t("hub.indicators.status")}: ${statut}`);
    return {
      brand: t("hub.indicators.exportBrand"),
      title: t("hub.indicators.title"),
      subtitle: t("hub.indicators.exportSubtitle"),
      generatedAtLabel: t("hub.indicators.exportGeneratedAt", {
        date: new Intl.DateTimeFormat(locale, {
          dateStyle: "long",
          timeStyle: "short",
        }).format(new Date()),
      }),
      filterSummary: filterParts.length
        ? t("hub.indicators.exportFilters", { filters: filterParts.join(" · ") })
        : t("hub.indicators.exportFiltersAll"),
      columns: {
        indicateur: t("hub.indicators.colIndicator"),
        service: t("hub.indicators.colService"),
        frequence: t("hub.indicators.colFrequency"),
        valeur: t("hub.indicators.colValue"),
        unite: t("hub.indicators.colUnit"),
      },
      footerNote: t("hub.indicators.exportFooter"),
    };
  }, [categorie, serviceId, frequence, statut, serviceOptions, t, locale]);

  const handleExport = useCallback(
    async (format: "pdf" | "word" | "excel") => {
      setExportError(null);
      setExporting(format);
      try {
        const result = await client.query({
          query: INDICATORS_CATALOG_QUERY,
          variables: {
            filters,
            first: 500,
            offset: 0,
            sort: "libelle_asc",
          },
          fetchPolicy: "network-only",
        });
        const list = ((result.data?.indicatorCatalog?.nodes ?? []) as CatalogNode[])
          .slice()
          .sort((a, b) =>
            a.libelle.localeCompare(b.libelle, "fr", { sensitivity: "base", numeric: true }),
          );
        const rows: ExportIndicatorRow[] = list.map((row) => ({
          libelle: row.libelle,
          serviceLibelle: row.serviceLibelle?.trim() || "—",
          frequence: row.frequence?.trim() || "—",
          valeur: "—",
          unite: row.unite?.trim() || "—",
        }));
        const meta = buildExportMeta();
        const stamp = new Date().toISOString().slice(0, 10);
        if (format === "pdf") {
          await exportIndicatorsPdf(rows, meta, `matrice-indicateurs-se-${stamp}.pdf`);
        } else if (format === "word") {
          await exportIndicatorsWord(rows, meta, `matrice-indicateurs-se-${stamp}.docx`);
        } else {
          await exportIndicatorsExcel(rows, meta, `matrice-indicateurs-se-${stamp}.xlsx`);
        }
      } catch {
        setExportError(t("hub.indicators.exportError"));
      } finally {
        setExporting(null);
      }
    },
    [client, filters, buildExportMeta, t],
  );

  return (
    <div className="nbs-hub-page" data-testid="hub-indicators-page">
      <section className="nbs-hub-section">
        <HubPageTitle>{t("hub.indicators.title")}</HubPageTitle>
        <p className="nbs-hub-collect__lead" data-testid="hub-indicators-gender-note">
          {t("hub.indicators.genderNote")}
        </p>

        {error ? (
          <div className="nbs-hub-banner nbs-hub-banner--danger" role="alert">
            <Text>{t("hub.indicators.loadError")}</Text>
            <button type="button" className="nbs-hub-btn" onClick={() => void refetch()}>
              {t("hub.overview.retry")}
            </button>
          </div>
        ) : null}

        <div className="nbs-hub-indicators">
          <aside className="nbs-hub-filters" aria-label={t("hub.indicators.filters")}>
            <p className="nbs-hub-filters__title">{t("hub.indicators.filters")}</p>

            <div className="nbs-hub-filters__field">
              <span>{t("hub.indicators.seCategory")}</span>
              <HubSearchSelect
                ariaLabel={t("hub.indicators.seCategory")}
                value={draftCategorie}
                options={categoryOptions}
                onChange={(value) => {
                  setDraftCategorie(value);
                  setDraftServiceId("");
                }}
              />
            </div>
            <div className="nbs-hub-filters__field">
              <span>{t("hub.indicators.service")}</span>
              <HubSearchSelect
                ariaLabel={t("hub.indicators.service")}
                value={serviceFilterDisabled ? "" : draftServiceId}
                options={serviceOptions}
                onChange={setDraftServiceId}
                disabled={serviceFilterDisabled}
              />
            </div>
            <div className="nbs-hub-filters__field">
              <span>{t("hub.indicators.frequency")}</span>
              <HubSearchSelect
                ariaLabel={t("hub.indicators.frequency")}
                value={draftFrequence}
                options={frequencyOptions}
                onChange={setDraftFrequence}
              />
            </div>
            <div className="nbs-hub-filters__field">
              <span>{t("hub.indicators.status")}</span>
              <HubSearchSelect
                ariaLabel={t("hub.indicators.status")}
                value={draftStatut}
                options={statusOptions}
                onChange={setDraftStatut}
              />
            </div>

            <div className="nbs-hub-filters__actions">
              <button type="button" className="nbs-hub-btn" onClick={applyFilters}>
                {t("hub.indicators.apply")}
              </button>
              <button
                type="button"
                className="nbs-hub-btn nbs-hub-btn--ghost"
                onClick={resetFilters}
              >
                {t("hub.indicators.reset")}
              </button>
            </div>
            {summary ? (
              <p className="nbs-hub-filters__meta">
                <span>
                  {summary.withDataCount}/{summary.totalCount} avec données
                </span>
                <span>
                  couverture{" "}
                  {new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
                    (summary.coverageRatio ?? 0) * 100,
                  )}
                  %
                </span>
              </p>
            ) : null}
          </aside>

          <div className="nbs-hub-card nbs-hub-table-panel">
            <div className="nbs-hub-export-bar" data-testid="hub-indicators-export">
              <p className="nbs-hub-export-bar__label">{t("hub.indicators.exportLabel")}</p>
              <div className="nbs-hub-export-bar__actions">
                <button
                  type="button"
                  className="nbs-hub-btn"
                  disabled={exporting !== null || totalCount <= 0}
                  onClick={() => void handleExport("pdf")}
                >
                  {exporting === "pdf"
                    ? t("hub.indicators.exporting")
                    : t("hub.indicators.exportPdf")}
                </button>
                <button
                  type="button"
                  className="nbs-hub-btn nbs-hub-btn--ghost"
                  disabled={exporting !== null || totalCount <= 0}
                  onClick={() => void handleExport("word")}
                >
                  {exporting === "word"
                    ? t("hub.indicators.exporting")
                    : t("hub.indicators.exportWord")}
                </button>
                <button
                  type="button"
                  className="nbs-hub-btn nbs-hub-btn--ghost"
                  disabled={exporting !== null || totalCount <= 0}
                  onClick={() => void handleExport("excel")}
                >
                  {exporting === "excel"
                    ? t("hub.indicators.exporting")
                    : t("hub.indicators.exportExcel")}
                </button>
              </div>
              {exportError ? (
                <p className="nbs-hub-export-bar__error" role="alert">
                  {exportError}
                </p>
              ) : null}
            </div>
            <div
              className={`nbs-hub-table-wrap${paging ? " nbs-hub-table-wrap--paging" : ""}`}
              data-page={page}
            >
              {initialLoading ? (
                <p className="nbs-hub-muted">{t("hub.indicators.loading")}</p>
              ) : !paging && nodes.length === 0 ? (
                <div className="nbs-hub-table-empty" data-testid="hub-indicators-empty">
                  <span className="nbs-hub-table-empty__icon" aria-hidden="true">
                    <svg viewBox="0 0 48 48" width="48" height="48" fill="none">
                      <rect
                        x="8"
                        y="10"
                        width="32"
                        height="28"
                        rx="4"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M16 20h16M16 26h10M16 32h14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="34"
                        cy="34"
                        r="9"
                        fill="var(--hub-card, #fff)"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M34 30v5M31.5 35.5 34 38l2.5-2.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <p className="nbs-hub-table-empty__title">{t("hub.indicators.empty")}</p>
                  <button type="button" className="nbs-hub-btn" onClick={resetFilters}>
                    {t("hub.indicators.reset")}
                  </button>
                </div>
              ) : (
                <table className="nbs-hub-table" key={`hub-ind-page-${page}`}>
                  <thead>
                    <tr>
                      <th>{t("hub.indicators.colIndicator")}</th>
                      <th>{t("hub.indicators.colService")}</th>
                      <th>{t("hub.indicators.colFrequency")}</th>
                      <th className="nbs-hub-table__last-value">{t("hub.indicators.colValue")}</th>
                      <th>{t("hub.indicators.colUnit")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodes.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.libelle}</strong>
                          <small>{row.categorie ?? "—"}</small>
                        </td>
                        <td>{row.serviceLibelle ?? "—"}</td>
                        <td>{row.frequence ?? "—"}</td>
                        <td className="nbs-hub-table__last-value">—</td>
                        <td>{row.unite?.trim() ? row.unite : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {totalCount > 0 ? (
              <footer className="nbs-hub-pager" data-testid="hub-indicators-pager">
                <p className="nbs-hub-pager__meta">
                  {t("hub.indicators.pagerMeta", {
                    total: new Intl.NumberFormat(locale).format(totalCount),
                    current: page + 1,
                    pages: totalPages,
                  })}
                </p>
                <div className="nbs-hub-pager__actions">
                  <button
                    type="button"
                    className="nbs-hub-btn nbs-hub-btn--ghost"
                    disabled={page <= 0 || paging}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    {t("hub.indicators.prev")}
                  </button>
                  <button
                    type="button"
                    className="nbs-hub-btn nbs-hub-btn--ghost"
                    disabled={page + 1 >= totalPages || paging}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    {t("hub.indicators.next")}
                  </button>
                </div>
              </footer>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
